/**
 * Login Page - Glass UI
 * Standalone authentication page with cosmic mesh background
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Envelope, LockKey, ArrowRight } from '@phosphor-icons/react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);

  const { login, isAuthenticated, error, sessionBootstrapped } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    const reason = (location.state as any)?.reason;
    if (reason === 'session_timeout') {
      setSessionMessage('Your session expired due to inactivity. Please log in again.');
      const timer = setTimeout(() => setSessionMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  if (!sessionBootstrapped) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1565c0] mx-auto" />
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Restoring session…</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    const from = (location.state as any)?.from?.pathname || '/control-tower';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(email, password);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen bg-cosmic-mesh flex items-center justify-center text-slate-800 antialiased overflow-hidden">
      <div className="mesh-light" />

      <div className="glass-card-premium w-full max-w-[420px] p-10 relative overflow-hidden flex flex-col items-center animate-scale-in">
        {/* Top gradient accent bar */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600" />

        {/* Logo with glow */}
        <div className="mb-8 relative group">
          <div className="absolute -inset-1 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-[26px] opacity-30 blur-lg transition duration-1000 group-hover:opacity-50 group-hover:duration-200" />
          <img
            src="/assets/fluent-ai-logo.png"
            alt="Fluent.ai Logo"
            className="relative h-28 w-auto rounded-[22px] shadow-2xl object-cover border-[4px] border-white/80 dark:border-white/10 bg-slate-900"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div className="relative h-28 w-28 rounded-[22px] shadow-2xl border-[4px] border-white/80 bg-slate-900 hidden items-center justify-center flex-col text-white">
            <span className="text-3xl font-black mb-1">F</span>
            <span className="text-[10px] font-bold">FLUENT.AI</span>
          </div>
        </div>

        {/* Subtitle */}
        <h2 className="text-xs font-black text-slate-500 tracking-widest uppercase mb-8">
          Agent Orchestration Platform
        </h2>

        {/* Session timeout warning */}
        {sessionMessage && (
          <div className="warn-banner w-full mb-6 border-2 rounded-2xl p-4">
            <p className="text-xs font-bold text-center">{sessionMessage}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="w-full space-y-5">
          <div>
            <label htmlFor="email" className="input-label">
              Email Address
            </label>
            <div className="relative group">
              <Envelope
                size={20}
                weight="bold"
                className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-slate-700 dark:group-focus-within:text-slate-200 transition-colors"
              />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field has-icon"
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="input-label">
              Password
            </label>
            <div className="relative group">
              <LockKey
                size={20}
                weight="bold"
                className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-slate-700 dark:group-focus-within:text-slate-200 transition-colors"
              />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field has-icon"
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          {/* Remember me + Forgot password */}
          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400/40 dark:text-slate-100"
              />
              <span className="text-xs font-bold text-slate-600">Remember me</span>
            </label>
            <button type="button" className="text-xs font-black text-appleBlue hover:text-blue-700 hover:underline transition-colors">
              Forgot password?
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 dark:border-red-500/30 rounded-2xl p-4">
              <p className="text-red-600 text-xs font-bold text-center">{error}</p>
            </div>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full mt-4 rounded-2xl py-3.5 h-auto"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Signing in...
              </>
            ) : (
              <>
                Sign In
                <ArrowRight
                  size={18}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </>
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 w-full text-center border-t border-slate-200">
          <p className="text-[10px] font-bold text-slate-400 tracking-tight">
            {__APP_QUARTER__} - v{__APP_VERSION__} &bull; Authorized Access Only
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
